import { FontFaceMapType, QuestionChoiceModified, QuestionOptionKey, QuestionOptionType, SurveyConfigurationType, SurveyEditType, SurveyEndPageType, SurveyIntegrationType, SurveyMetaVariableType, SurveyNotificationsType, SurveyQuestionOptionType, SurveyQuestionType, SurveyShareMode, SurveyState, SurveyStyleType, SurveyStylingBlockStyleType, SurveyStylingType } from "@/common/interfaces";
import { defineStore } from "pinia";
import { h, Ref, ref } from "vue";
import { Configuration } from "../configuration";
import { EditorQuestion } from "./questions";
import { SurveyQuestionEditEvent, SurveyQuestionOptionEvent } from "../messages";
import { EditorPage } from "./pages";
import { EditableSurveyLogicRule, SurveyLogicRule } from "../logic/types";
import { FontManager } from "@/common/fonts";
import { Full, KeysOfType } from "@/common/types";
import { assertUnreachable } from "@/common/utils";
import { isEmptySurveyDoc, isEmptySurveyDocJson } from "@/components/nss-editor/utils/generic";
import { schema } from "@/components/nss-editor/schema";

export declare type LiveDoc = {
	doc: {[key: string]: any};
	version: number;
	clientID: string;
};

export declare type QuestionOptionValues = {
	intValue?: number | null;
	charValue?: string | null;
	textValue?: string | null;
	datetimeValue?: string | null;
	jsonValue?: {[key: string]: any} | null;
};

export type SurveyStylingKeys = KeysOfType<Full<SurveyStylingType>, SurveyStylingBlockStyleType>;
export type SurveyStyleCssEntry = {for: keyof SurveyStylingType, styles: {[key: string]: string}};

export const useSurveyEditStore = defineStore('surveyEdit', () => {
	const consumerChannel: Ref<string | null> = ref(null);

	const name: Ref<string> = ref('');
	const emoji: Ref<string | null> = ref(null);
	const isTemplate: Ref<boolean> = ref(false);
	const doc: Ref<{[key: string]: any} | null> = ref(null);
	const info: Ref<SurveyEditType | null> = ref(null);
	const configuration: Ref<Configuration | null> = ref(null);
	const style: Ref<SurveyStyleType | null> = ref(null);
	const styleCss: Ref<Array<SurveyStyleCssEntry>> = ref([]);
	const loaded: Ref<boolean> = ref(false);

	const notifications: Ref<SurveyNotificationsType | null> = ref(null);

	const questions: Ref<{[key: string]: EditorQuestion}> = ref({});

	const pages: Ref<{[key: string]: EditorPage}> = ref({});

	const liveDoc: Ref<LiveDoc | null> = ref(null);

	const editingSurvey: Ref<boolean> = ref(false);

	const inEditMode: Ref<boolean> = ref(false);

	const questionUuidHasFocus: Ref<string | null> = ref(null);

	const logic: Ref<Array<EditableSurveyLogicRule> | null> = ref([]);

	const availableFonts: Ref<{[key: string]: FontFaceMapType}> = ref({});

	const endPages: Ref<Array<SurveyEndPageType>> = ref([]);

	const metaVariables: Ref<Array<SurveyMetaVariableType>> = ref([]);

	const integrations: Ref<Array<SurveyIntegrationType>> = ref([]);

	const aiToolOpen: Ref<'analyze' | null> = ref(null);

	const isBlankSurvey: Ref<boolean> = ref(false);

	function newDefaultSurveyStyle(): SurveyStyleType {
		return {
			logo: null,
			styling: {}
		};
	}

	function clearStore() {
		name.value = '';
		doc.value = null;
		info.value = null;
		emoji.value = null;
		isTemplate.value = false;
		loaded.value = false;
		configuration.value = null;
		liveDoc.value = null;
		editingSurvey.value = false;
		questions.value = {};
		notifications.value = null;
		pages.value = {};
		logic.value = null;
		style.value = newDefaultSurveyStyle();
		endPages.value = [];
		metaVariables.value = [];
		integrations.value = [];

		// Reset doc css vars
		document.documentElement.style.setProperty('--srv-body-font', 'Inter, sans-serif');
		document.documentElement.style.setProperty('--srv-heading-font', 'Inter, sans-serif');
		document.documentElement.style.setProperty('--srv-title-font', 'Inter, sans-serif');
		document.documentElement.style.setProperty('--srv-text-raw', '0, 0, 0');
		document.documentElement.style.setProperty('--srv-accent-raw', '0, 0, 0');
		document.documentElement.style.setProperty('--srv-heading-color', 'rgb(0, 0, 0)');
		document.documentElement.style.setProperty('--srv-title-color', 'rgb(0, 0, 0)');
		document.documentElement.style.setProperty('--srv-background', 'rgb(255, 255, 255)');
		document.documentElement.style.setProperty('--srv-button-background', 'rgb(0, 0, 0)');
		document.documentElement.style.setProperty('--srv-button-text', 'rgb(255, 255, 255)');
	}

	function setAvailableFonts(availableFonts_: {[key: string]: FontFaceMapType}) {
		availableFonts.value = availableFonts_;
		regenerateStylingCss();
	}

	function setStyle(newStyle: SurveyStyleType) {
		style.value = newStyle;
		regenerateStylingCss();
	}

	function setEditMode(editing: boolean) {
		inEditMode.value = editing;
	}

	function updateSurvey(survey: SurveyEditType) {
		info.value = survey;
		name.value = survey.name;
		emoji.value = survey.emoji;
		isTemplate.value = survey.isTemplate;
	}

	function setStore(
		surveyInfo: {
			survey: SurveyEditType,
			configuration: SurveyConfigurationType,
			questions: Array<SurveyQuestionType>,
			style: SurveyStyleType | null,
			endPages: Array<SurveyEndPageType>,
			metaVariables: Array<SurveyMetaVariableType>,
			integrations: Array<SurveyIntegrationType>
		}
	) {
		clearStore();
		info.value = surveyInfo.survey;
		name.value = surveyInfo.survey.name;
		emoji.value = surveyInfo.survey.emoji;
		isTemplate.value = surveyInfo.survey.isTemplate;
		doc.value = surveyInfo.survey.surveyDoc;
		logic.value = surveyInfo.survey.logic as Array<SurveyLogicRule>;
		if (surveyInfo.style !== null) {
			style.value = surveyInfo.style;
		} else {
			style.value = newDefaultSurveyStyle();
		}
		endPages.value = surveyInfo.endPages;
		metaVariables.value = surveyInfo.metaVariables;
		integrations.value = surveyInfo.integrations;
		questions.value = {};
		for (const question of surveyInfo.questions) {
			storeEditorQuestionFromRemote(question);
		}
		updateConfiguration(surveyInfo.configuration);
		calculatePages();
		regenerateStylingCss();
		loaded.value = true;
	}

	/**
	 * While editing a survey pages aren't stored objects.
	 * They exist in the doc but not the database.
	 *
	 * It's only on publish they exist. This creates a mapping
	 * of the "placeholder" pages that exist in the document
	 * structure. So we can map questions to them or the other
	 * way around elsewhere.
	 **/
	function calculatePages() {
		if (doc.value === null) {
			return;
		}

		pages.value = {};

		let currentPage = new EditorPage(
			'firstPage', 0, null
		);

		let pageIndex = 1;
		for (const blockInfo of doc.value.content) {
			if (blockInfo.content && blockInfo.content[0].type === 'page_separator') {
				pages.value[currentPage.uuid] = currentPage;
				currentPage = new EditorPage(
					blockInfo.content[0].attrs.uuid,
					pageIndex,
					blockInfo.content[0].attrs.name
				);
				pageIndex += 1;
			}
			currentPage.addBlock(blockInfo);
		}
		// Theres always a final page separator so this might be skipable?
		//if (!(currentPage.uuid in pages.value)) {
		//	pages.value[currentPage.uuid] = currentPage;
		//}
	}

	function upsertLogicRule(rule: EditableSurveyLogicRule, dontChangePublishStatus: boolean = false): void {
		if (logic.value === null) {
			logic.value = [rule];
			return;
		}

		const existingRuleIndex = logic.value.findIndex(
			(er: EditableSurveyLogicRule) => er.uuid === rule.uuid
		);

		if (existingRuleIndex > -1) {
			logic.value.splice(existingRuleIndex, 1, rule);
		} else {
			logic.value.push(rule);
		}

		if (info.value && !dontChangePublishStatus) info.value.hasUnpublishedChanges = true;
	}

	function removeLogicRule(ruleUuid: string, dontChangePublishStatus: boolean = false): void {
		if (logic.value === null) {
			return;
		}

		const index = logic.value.findIndex(
			(rule: EditableSurveyLogicRule) => rule.uuid === ruleUuid
		);

		if (index > -1) {
			logic.value.splice(index, 1);
		}

		if (info.value && !dontChangePublishStatus) info.value.hasUnpublishedChanges = true;
	}

	function setLogic(newLogic: Array<SurveyLogicRule> | null): void {
		logic.value = newLogic;
	}

	function getOrderedPages(): Array<EditorPage> {
		if (pages.value === null) {
			return [];
		}

		return Object.values(pages.value).sort((a, b) => a.index - b.index);
	}

	function getPageAndQuestionByQuestionUuid(questionUuid: string): [EditorPage, EditorQuestion] | null {
		const editorPage = Object.values(pages.value).find((ep: EditorPage) => ep.questionUuids.includes(questionUuid));

		if (editorPage === undefined) {
			return null;
		}

		const question = getQuestion(questionUuid);

		if (question === null) {
			return null;
		}

		return [editorPage, question];
	}

	function setCurrentDoc(
		doc: {[key: string]: any},
		version: number
	) {
		if (consumerChannel.value === null) {
			console.error("Attempting to set liveDoc when consumer is not ready!");
			throw new Error("Cannot setCurrentDoc without valid consumer");
		}

		liveDoc.value = {doc, version, clientID: consumerChannel.value};
	}

	function setSurveyName(
		newName: string
	) {
		if (info.value === null) {
			return;
		}

		info.value.name = newName;
		name.value = newName;
	}

	function setTemplate(_isTemplate: boolean): void {
		isTemplate.value = _isTemplate;
	}

	function setSurveyEmoji(newEmoji: string | null) {
		if (info.value === null) {
			return;
		}

		info.value.emoji = newEmoji;

		emoji.value = newEmoji;
	}

	function setSurveyDoc(
		newDoc: {[key: string]: any}
	) {
		if (info.value === null) {
			return;
		}

		info.value.surveyDoc = newDoc;
		doc.value = newDoc;

		isBlankSurvey.value = isEmptySurveyDocJson(newDoc, schema);

		calculatePages();
	}

	const setConsumerChannel = (channel: string): void => {
		consumerChannel.value = channel;
	};

	const updateConfiguration = (newConfiguration: SurveyConfigurationType): void => {
		if (configuration.value === null) {
			configuration.value = new Configuration(newConfiguration);
		} else {
			configuration.value.update(newConfiguration);
		}
	}

	function setShareMode(newMode: SurveyShareMode) {
		if (info.value === null) {
			return;
		}

		info.value.shareMode = newMode;
	}

	function setState(newState: SurveyState) {
		if (info.value === null) {
			return;
		}

		info.value.state = newState;
	}

	function storeEditorQuestionFromRemote(question: SurveyQuestionType): void {
		questions.value[question.uuid] = EditorQuestion.fromRemote(question);
	}

	function removeEditorQuestion(questionUuid: string): void {
		if (questions.value[questionUuid] !== undefined) {
			delete questions.value[questionUuid];
		}
	}

	function updateEditorQuestionOptionsFromEvent(updateInfo: SurveyQuestionOptionEvent): void {
		const question = questions.value[updateInfo.question.uuid];

		if (question) {
			question.updateOptions(updateInfo.question);
		}
	}

	function updateEditorQuestionOptionDirect(
		questionUuid: string, key: QuestionOptionKey, optionType: QuestionOptionType, values: QuestionOptionValues
	): void {
		const question = questions.value[questionUuid];

		if (values.charValue === undefined) {
			values.charValue = null;
		}
		if (values.intValue === undefined) {
			values.intValue = null;
		}
		if (values.datetimeValue === undefined) {
			values.datetimeValue = null;
		}
		if (values.textValue === undefined) {
			values.textValue = null;
		}
		if (values.jsonValue === undefined) {
			values.jsonValue = null;
		}

		if (question) {
			question.options.set(key, {
				key, optionType, ...values
			} as SurveyQuestionOptionType);
		}

		if (info.value) info.value.hasUnpublishedChanges = true;
	}

	function updateEditorQuestionFromEvent(updateInfo: SurveyQuestionEditEvent): void {
		const question = questions.value[updateInfo.questionUuid];

		let lameUpdate = false; // Some of these aren't persistent fields, label for example
		if (question) {
			switch(updateInfo.field) {
				case 'required':
					question.required = updateInfo.value;
				break;
				case 'identifier':
					question.identifier = updateInfo.value;
				break;
				case 'choiceBadge':
					question.choiceBadge = updateInfo.value;
				break;
				case 'displayStyle':
					question.displayStyle = updateInfo.value;
				break;
				case 'randomizeChoices':
					question.randomizeChoices = updateInfo.value;
				break;
				case 'limitMin':
					question.limitMin = updateInfo.value;
				break;
				case 'limitMax':
					question.limitMax = updateInfo.value;
				break;
				case 'bulkChoices':
					question.bulkChoices = updateInfo.value;
				break;
				case 'label':
					question.label = updateInfo.value;
					lameUpdate = true;
				break;
				case 'visible':
					question.visible = updateInfo.value;
				break;
			}
		}

		if (info.value && !lameUpdate) info.value.hasUnpublishedChanges = true;
	}

	function updateEditorQuestionChoices(questionUuid: string, choiceModifications: Array<QuestionChoiceModified>): void {
		if (questions.value[questionUuid] === undefined) {
			return;
		}

		questions.value[questionUuid].updateChoices(choiceModifications);

		if (info.value) info.value.hasUnpublishedChanges = true;
	}

	function getQuestion(questionUuid: string): EditorQuestion | null {
		const question = questions.value[questionUuid];

		return (question)? question : null;
	}

	function setNotifications(notifications_: SurveyNotificationsType): void {
		notifications.value = notifications_;
	}

	function regenerateStylingCss(): void {
		if (style.value === null) {
			styleCss.value = [];
			return;
		}

		const result: Array<SurveyStyleCssEntry> = [];
		for (const stylingKey of ['headings', 'questionTitles', 'body'] as Array<SurveyStylingKeys>) {
			const stylingInfo = style.value.styling[stylingKey];
			if (stylingInfo === undefined) {
				continue;
			}

			let styles: {[key: string]: string} = {};
			if (stylingInfo.fontFamilyKey) {
				const fontFaceInfo = availableFonts.value[stylingInfo.fontFamilyKey];
				if (fontFaceInfo) {
					FontManager.loadFontFamily(stylingInfo.fontFamilyKey, fontFaceInfo);
					styles['font-family'] = `${stylingInfo.fontFamilyKey}, Inter, sans-serif`;
				}
			}

			result.push({
				for: stylingKey,
				styles
			});
		}

		styleCss.value = result;
	}

	function setStylingProperty(
		stylingKey: keyof SurveyStylingType,
		property: string,
		value: any
	): void {
		if (style.value === null) {
			style.value = {
				logo: null,
				styling: {}
			};
		}

		if (style.value.styling[stylingKey] === undefined) {
			style.value.styling[stylingKey] = {
				[property]: value
			};
		} else {
			//@ts-ignore
			style.value.styling[stylingKey][property] = value;
		}

		regenerateStylingCss();
	}

	function upsertEndPage(endPage: SurveyEndPageType): void {
		const foundEndPageIndex = endPages.value.findIndex(
			(ep: SurveyEndPageType) => ep.uuid === endPage.uuid
		);

		if (foundEndPageIndex > -1) {
			endPages.value[foundEndPageIndex] = endPage;
		} else {
			endPages.value.push(endPage);
		}
	}

	function removeEndPage(endPageUuid: string): void {
		const foundEndPageIndex = endPages.value.findIndex(
			(ep: SurveyEndPageType) => ep.uuid === endPageUuid
		);

		if (foundEndPageIndex > -1) {
			endPages.value.splice(foundEndPageIndex, 1);
		}
	}

	function upsertMetaVariable(variable: SurveyMetaVariableType): void {
		const existingIndex = metaVariables.value.findIndex(
			(mv: SurveyMetaVariableType) => mv.uuid === variable.uuid
		);

		if (existingIndex === -1) {
			metaVariables.value.push(variable);
		} else {
			metaVariables.value.splice(existingIndex, 1, variable);
		}
	}

	function removeMetaVariable(variableUuid: string): void {
		const existingIndex = metaVariables.value.findIndex(
			(mv: SurveyMetaVariableType) => mv.uuid === variableUuid
		);

		if (existingIndex === -1) {
			return;
		}

		metaVariables.value.splice(existingIndex, 1);
	}

	function upsertIntegration(integration: SurveyIntegrationType): void {
		const existingIndex = integrations.value.findIndex(
			(si: SurveyIntegrationType) => si.uuid === integration.uuid
		);

		if (existingIndex > -1) {
			integrations.value.splice(existingIndex, 1, integration);
		} else {
			integrations.value.push(integration);
		}
	}

	function removeIntegration(integrationUuid: string): void {
		const index = integrations.value.findIndex(
			(si: SurveyIntegrationType) => si.uuid === integrationUuid
		);

		if (index > -1) {
			integrations.value.splice(index, 1);
		}
	}

	return {
		name, emoji, doc, info, loaded, liveDoc, consumerChannel, configuration, notifications,
		aiToolOpen, isBlankSurvey,
		style, setStyle, setStylingProperty, styleCss,
		endPages, upsertEndPage, removeEndPage,
		pages, getOrderedPages, getPageAndQuestionByQuestionUuid,
		availableFonts, setAvailableFonts,
		isTemplate, setTemplate, updateSurvey, editingSurvey,
		setSurveyName, setSurveyDoc, setCurrentDoc, setSurveyEmoji,
		clearStore, setStore, setConsumerChannel,
		getQuestion, updateEditorQuestionFromEvent,
		storeEditorQuestionFromRemote, removeEditorQuestion, updateEditorQuestionChoices,
		updateEditorQuestionOptionsFromEvent, updateEditorQuestionOptionDirect,
		updateConfiguration,
		setShareMode, setState,
		setNotifications,
		metaVariables, upsertMetaVariable, removeMetaVariable,
		inEditMode, setEditMode,
		questionUuidHasFocus,
		upsertLogicRule, setLogic, logic, removeLogicRule,
		integrations, upsertIntegration, removeIntegration
	};
});
